/**
 * Session kill switch for course versioning. Every versioning endpoint answers
 * `404 { code: 'VERSIONING_OFF' }` while the org flag is off; the first such
 * answer disables the feature for the rest of the session (the Designer falls
 * back to the legacy Save Changes flow, learner pages skip What's new) with a
 * single console.warn and no toast. `useCourseVersioning` subscribes to it.
 */
let disabled = false;
const listeners = new Set<() => void>();

export const isCourseVersioningDisabledForSession = (): boolean => disabled;

export const subscribeCourseVersioningSession = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const disableCourseVersioningForSession = (source?: string): void => {
  if (disabled) return;
  disabled = true;
  // eslint-disable-next-line no-console
  console.warn(
    `[course-versioning] ${
      source ? `${source}: ` : ''
    }the API answered VERSIONING_OFF — versioning is off for this session`
  );
  listeners.forEach((listener) => listener());
};

export const isVersioningOffError = (err: unknown): boolean => {
  const e = err as { response?: { status?: number; data?: { code?: string } } } | undefined;
  return e?.response?.status === 404 && e?.response?.data?.code === 'VERSIONING_OFF';
};

/**
 * Wraps a versioning API call: a VERSIONING_OFF answer disables the feature
 * for the session before the error reaches the caller.
 */
export const withVersioningOffGuard = <T>(source: string, call: Promise<T>): Promise<T> =>
  call.catch((err) => {
    if (isVersioningOffError(err)) disableCourseVersioningForSession(source);
    throw err;
  });
